import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { ShieldCheck, MessageSquare, Send, Phone } from 'lucide-react'
import ContactForm from '../components/ContactForm'
import SEO from '../components/SEO'

export default function Contact() {
  const [searchParams] = useSearchParams()
  const defaultServiceParam = searchParams.get('service') || searchParams.get('event') || searchParams.get('type') || ''

  const contactSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ContactPage",
        "@id": "https://www.djron9.dk/kontakt/#webpage",
        "url": "https://www.djron9.dk/kontakt/",
        "name": "Kontakt DJ Ron9 | Få et uforpligtende tilbud på DJ",
        "description": "Kontakt DJ Ron9 for booking og tilbud til bryllup, firmafest eller fødselsdag. Ring på 50 99 82 82 eller send en forespørgsel online.",
        "breadcrumb": {
          "@id": "https://www.djron9.dk/kontakt/#breadcrumb"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://www.djron9.dk/kontakt/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://www.djron9.dk/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Kontakt",
            "item": "https://www.djron9.dk/kontakt/"
          }
        ]
      },
      {
        "@type": "EntertainmentBusiness",
        "name": "DJ Ron9",
        "url": "https://www.djron9.dk/",
        "telephone": "+4550998282",
        "email": "ronni@djron9.dk",
        "address": {
          "@type": "PostalAddress",
          "postalCode": "2100",
          "addressLocality": "København Ø",
          "addressCountry": "DK"
        }
      }
    ]
  }

  return (
    <div className="bg-black text-white">
      <SEO
        title="Kontakt DJ Ron9 | Få et uforpligtende tilbud på DJ"
        description="Kontakt DJ Ron9 for booking og tilbud til bryllup, firmafest eller fødselsdag. Ring på 50 99 82 82 eller send en forespørgsel online."
        canonical="/kontakt/"
        ogImage="https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/6a6f183f-a8d2-4ab1-ba69-46555a150cfd.jpg"
        schema={contactSchema}
      />
      {/* Hero Section */}
      <section className="relative flex min-h-[35vh] sm:min-h-[50vh] items-center justify-center overflow-hidden py-12 sm:py-20">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/6a6f183f-a8d2-4ab1-ba69-46555a150cfd.jpg"
            alt="Kontakt DJ Ron9"
            width="1600"
            height="500"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover object-[center_20%] opacity-75 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl w-full px-4 text-left sm:px-6 lg:px-8">
          <h1 className="mt-2 font-serif text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl text-white">
            Kontakt DJ Ron9
          </h1>
          <p className="mt-4 max-w-2xl text-base text-zinc-300 sm:text-lg leading-relaxed">
            Klar til at planlægge din næste store fest? Kontakt mig i dag og få et skræddersyet tilbud.
          </p>

          {/* Contact CTA Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-start gap-4">
            <button
              onClick={() => document.getElementById('kontakt-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#bb9b56] px-8 py-3.5 text-sm font-bold text-white shadow-lg hover:bg-[#a88645] transition-all cursor-pointer"
            >
              <Send className="h-4 w-4" />
              Udfyld kontaktformularen
            </button>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20 bg-black">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
            
            {/* Contact Information Column */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="mt-2 font-serif text-3xl font-semibold tracking-tight sm:text-4xl text-white">
                  Jeg er klar til at hjælpe dig
                </h2>
                <p className="mt-4 text-zinc-400 leading-relaxed text-sm sm:text-base">
                  Uanset om du planlægger et bryllup, en rund fødselsdag, en julefrokost eller en stor firmafest, står jeg klar til at levere musikken og udstyret, der gør aftenen helt speciel.
                </p>
              </div>

              {/* Company Info Footer Card */}
              <div className="rounded-xl border border-zinc-900 bg-zinc-950/30 p-6 space-y-3 shadow-backlight-hover md:shadow-backlight md:hover:shadow-backlight-hover transition-all duration-300">
                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                  <ShieldCheck className="h-5 w-5 text-amber-400" />
                  <span>Virksomhedsoplysninger</span>
                </div>
                <div className="text-xs text-zinc-500 space-y-1 pl-7">
                  <p className="font-semibold text-zinc-400">DJ Ron9</p>
                  <p>2100 København Ø</p>
                  <p className="pt-2">CVR: 34924139</p>
                </div>
              </div>

            </div>

            {/* Contact Form Column */}
            <div id="kontakt-form" className="lg:col-span-7">
              <ContactForm defaultService={defaultServiceParam} />
            </div>

          </div>
        </div>
      </section>

      {/* FAQ Quick Link Section */}
      <section className="py-16 border-t border-zinc-900 bg-zinc-950/40">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-zinc-900 mb-4">
            <MessageSquare className="h-6 w-6 text-amber-400" />
          </div>
          <h2 className="font-serif text-2xl font-semibold text-white">Har du spørgsmål til booking?</h2>
          <p className="mt-2 text-zinc-400 text-sm sm:text-base max-w-2xl mx-auto">
            Jeg har samlet svar på de oftest stillede spørgsmål om alt fra spilletid og udstyr til aflysning og planlægning.
          </p>
          <div className="mt-6">
            <a
              href="/#faq"
              className="inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900/50 px-6 py-3 text-sm font-semibold text-zinc-300 hover:bg-zinc-900 hover:text-white transition-all"
            >
              Gå til Ofte Stillede Spørgsmål
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
